import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, Search, Ticket, Star, Clock } from 'lucide-react';
import { publicApi } from '@services/publicApi';
import { usePublicStore } from '@store/publicStore';
import { formatDate, formatCurrency } from '@utils';
import type { Event } from '@types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function EventCard({ event, index }: { event: Event; index: number }) {
  const minPrice = event.ticketTypes?.length
    ? Math.min(...event.ticketTypes.map(t => t.price))
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/events/${event.slug}`}
        className="group block bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      >
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30">
          {event.featuredImage ? (
            <img
              src={event.featuredImage}
              alt={event.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Calendar className="w-12 h-12 text-primary-300 dark:text-primary-700" />
            </div>
          )}
          {event.isFeatured && (
            <span className="absolute top-3 left-3 px-2.5 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center gap-1">
              <Star className="w-3 h-3" /> Featured
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 text-xs text-primary-600 dark:text-primary-400 font-medium mb-2">
            <Clock className="w-3.5 h-3.5" />
            {formatDate(event.startDate, 'EEE, MMM d, yyyy')}
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {event.name}
          </h3>
          {event.venue && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-3">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{event.venue.name}, {event.venue.city}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
            {minPrice !== null ? (
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {minPrice === 0 ? 'Free' : `From ${formatCurrency(minPrice, event.currency)}`}
              </span>
            ) : (
              <span className="text-sm text-gray-400">Sold Out</span>
            )}
            <span className="text-xs font-medium text-primary-600 dark:text-primary-400 flex items-center gap-1 group-hover:gap-2 transition-all">
              Get Tickets <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function PublicHomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['storefront'],
    queryFn: () => publicApi.getStorefront(),
    staleTime: 5 * 60 * 1000,
  });

  const storefront = data?.data;
  const featuredEvents = storefront?.featuredEvents || [];
  const upcomingEvents = storefront?.upcomingEvents || [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 w-[300px] h-[300px] bg-primary-400/15 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Discover Amazing{' '}
              <span className="text-primary-200">Events</span>
            </h1>
            <p className="text-lg sm:text-xl text-primary-100 mb-10 max-w-2xl mx-auto">
              Find and book tickets for the best events near you. Concerts, conferences, sports, and more.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search events, venues, categories..."
                  className="w-full pl-12 pr-32 py-4 rounded-2xl bg-white/95 backdrop-blur text-gray-900 placeholder-gray-400 text-base outline-none focus:ring-2 focus:ring-primary-400 shadow-xl"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Featured Events */}
      {featuredEvents.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Featured Events</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Don't miss these highlighted experiences</p>
            </div>
            <Link
              to="/events"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredEvents.map((event, i) => (
              <EventCard key={event.id} event={event} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Upcoming Events</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Browse what's coming up next</p>
            </div>
            <Link
              to="/events"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {upcomingEvents.map((event, i) => (
              <EventCard key={event.id} event={event} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {!isLoading && featuredEvents.length === 0 && upcomingEvents.length === 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-6">
            <Ticket className="w-10 h-10 text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Events Yet</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Check back soon for upcoming events and ticket sales.
          </p>
        </section>
      )}

      {/* Loading */}
      {isLoading && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
                <div className="h-48 bg-gray-100 dark:bg-gray-800 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                  <div className="h-5 w-3/4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
